import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Socket } from 'socket.io-client';
import { ToastrService } from 'ngx-toastr';
import { LaboratorioService } from 'src/app/services/laboratorio/laboratorio.service';

interface TomaRow {
  COD_TOMS?: any;
  DIR_SURC?: any;
  NOM_CLI?: any;
  EST_TOMS?: any;
  NUM_ORD?: any;
  FECREG_TOMS?: any;
  COD_ORD?: any;
  EST_TOM?: any;
  sucursal?: any;
  cliente?: any;
  estado?: any;
  orden?: any;
  fecha?: any;
  [key: string]: any;
}

@Component({
  selector: 'app-estados-muestras',
  templateUrl: './estados-muestras.component.html',
  styleUrls: ['./estados-muestras.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class EstadosMuestrasComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scannerInput') scannerInput?: ElementRef<HTMLInputElement>;

  readonly estados = ['Facturado', 'En laboratorio', 'Retirado'];
  readonly sucursales = [
    'PLAZA BOLIVARIANA',
    'CC ALBOCENTRO 4',
    'CC PLAZA PICCA VIA A DAULE',
    'PORTETE',
    'DURAN',
    'CC LAS TERRAZAS AV 25 JULIO',
    'CERRADO',
    'CC GARZOCENTRO (MATRIZ)',
    'BODEGA GENERAL',
    'CC PLAZA ORELLANA',
    'CC PLAZA CORONEL',
    'AGORA MEDICAL CENTER',
    'CC AVALON PLAZA',
    'BUENA VISTA.'
  ];

  readonly estadosToma: Record<number, { texto: string; color: string }> = {
    1: { texto: 'NORMAL', color: '#1e88e5' },
    2: { texto: 'PENDIENTES MUESTRAS', color: '#e53935' },
    3: { texto: 'EMERGENCIA', color: '#e53935' },
    4: { texto: 'DERIVADO', color: '#f9a825' },
    5: { texto: 'EN SUCURSAL', color: '#43a047' },
  };

  rows: TomaRow[] = [];
  detalleRows: any[] = [];
  selectedRow: TomaRow | null = null;
  loading = false;
  loadingDetalle = false;
  error = '';
  socketStatus = 'desconectado';

  filters = {
    codigo: '',
    sucursal: '',
    paciente: '',
    estado: '',
    fecha: '',
    tipo: '',
  };

  private socket?: Socket;
  private token = '';
  private audioContext?: AudioContext;

  constructor(
    private laboratorioService: LaboratorioService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.token = localStorage.getItem('token') || '';
    this.cargarItems();
    this.conectarSocket();
  }

  ngAfterViewInit(): void {
    this.focusScanner();
  }

  ngOnDestroy(): void {
    this.socket?.disconnect();
  }

  cargarItems(): void {
    this.loading = true;
    this.error = '';
    this.laboratorioService.listarEstadosMuestras(this.token).subscribe({
      next: (resp) => {
        this.rows = (resp?.rows || []).map((row) => this.normalizarRow(row));
        this.loading = false;
      },
      error: () => {
        this.error = 'No fue posible cargar los estados de muestras.';
        this.loading = false;
      }
    });
  }

  get filteredRows(): TomaRow[] {
    return this.rows.filter((row) => {
      return this.matches(row.COD_TOMS, this.filters.codigo)
        && this.matches(row.DIR_SURC, this.filters.sucursal, true)
        && this.matches(row.NOM_CLI, this.filters.paciente)
        && this.matches(row.EST_TOMS, this.filters.estado, true)
        && this.matches(this.formatFecha(row.FECREG_TOMS), this.filters.fecha)
        && this.matches(this.estadoTomaTexto(row.EST_TOM), this.filters.tipo);
    });
  }

  get totalRows(): number {
    return this.rows.length;
  }

  get totalFiltrados(): number {
    return this.filteredRows.length;
  }

  get totalFacturados(): number {
    return this.rows.filter((row) => this.normalize(row.EST_TOMS) === 'facturado').length;
  }

  get totalEnLaboratorio(): number {
    return this.rows.filter((row) => this.normalize(row.EST_TOMS) === 'en laboratorio').length;
  }

  get totalRetirados(): number {
    return this.rows.filter((row) => this.normalize(row.EST_TOMS) === 'retirado').length;
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filters).some((value) => String(value || '').trim() !== '');
  }

  abrirDetalle(row: TomaRow, modal: TemplateRef<any>): void {
    this.selectedRow = row;
    this.detalleRows = [];
    this.loadingDetalle = true;
    const modalRef = this.modalService.open(modal, {
      size: 'xl',
      centered: true,
      windowClass: 'sample-status-detail-modal'
    });
    modalRef.result.then(
      () => this.focusScanner(),
      () => this.focusScanner(),
    );

    this.laboratorioService.detalleOrdenToma(this.token, row.NUM_ORD).subscribe({
      next: (resp) => this.cargarProductosDetalle(resp?.rows || []),
      error: () => {
        this.loadingDetalle = false;
        this.detalleRows = [];
      }
    });
  }

  buscarConScanner(codigo: string, modal: TemplateRef<any>): void {
    const value = String(codigo || '').trim();
    if (!value) return;

    const row = this.rows.find((item) => String(item.COD_ORD || '') === value);
    if (row) {
      this.abrirDetalle(row, modal);
    }
  }

  limpiarFiltros(): void {
    this.filters = {
      codigo: '',
      sucursal: '',
      paciente: '',
      estado: '',
      fecha: '',
      tipo: '',
    };
    this.focusScanner();
  }

  estadoClass(estado: string): string {
    switch (this.normalize(estado)) {
      case 'facturado': return 'status-pill status-pill--facturado';
      case 'retirado': return 'status-pill status-pill--retirado';
      case 'en laboratorio': return 'status-pill status-pill--laboratorio';
      default: return 'status-pill status-pill--default';
    }
  }

  socketClass(): string {
    switch (this.socketStatus) {
      case 'conectado': return 'realtime-pill realtime-pill--online';
      case 'sin autorización': return 'realtime-pill realtime-pill--warning';
      default: return 'realtime-pill realtime-pill--offline';
    }
  }

  estadoTomaTexto(estadoId: any): string {
    const id = Number(estadoId);
    return this.estadosToma[id]?.texto || 'SIN ESTADO';
  }

  estadoTomaColor(estadoId: any): string {
    const id = Number(estadoId);
    return this.estadosToma[id]?.color || '#9e9e9e';
  }

  formatFecha(value: any): string {
    if (!value) return '';
    const fecha = new Date(value);
    if (Number.isNaN(fecha.getTime())) return String(value);
    const yyyy = fecha.getFullYear();
    const mm = String(fecha.getMonth() + 1).padStart(2, '0');
    const dd = String(fecha.getDate()).padStart(2, '0');
    const hh = String(fecha.getHours()).padStart(2, '0');
    const mi = String(fecha.getMinutes()).padStart(2, '0');
    const ss = String(fecha.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  }

  trackByToma(index: number, item: TomaRow): any {
    return item.COD_TOMS || item.COD_ORD || item.NUM_ORD || index;
  }

  private conectarSocket(): void {
    this.socket = this.laboratorioService.conectarEstadosMuestrasSocket();

    this.socket.on('connect', () => {
      this.zone.run(() => this.socketStatus = 'conectado');
    });

    this.socket.on('disconnect', () => {
      this.zone.run(() => this.socketStatus = 'desconectado');
    });

    this.socket.on('connect_error', () => {
      this.zone.run(() => this.socketStatus = 'sin autorización');
    });

    this.socket.on('nuevo_registro', (item: TomaRow) => {
      this.zone.run(() => this.upsertRow(item));
    });

    this.socket.on('estado_actualizado', ({ id, estado, fecha }: any) => {
      this.zone.run(() => {
        const row = this.rows.find((item) => Number(item.COD_TOMS) === Number(id));
        if (!row) return;
        row.EST_TOMS = estado;
        row.estado = estado;
        row.FECREG_TOMS = fecha;
        row.fecha = fecha;
        this.playTone(640, 0.07);
        this.toastr.info(`Toma ${id} actualizada a ${estado}`, 'Muestras', {
          timeOut: 2500,
          closeButton: true,
          progressBar: true,
        });
      });
    });

    this.socket.on('eliminar_toma', ({ id }: any) => {
      this.zone.run(() => {
        this.rows = this.rows.filter((item) => Number(item.COD_TOMS) !== Number(id));
      });
    });
  }

  private focusScanner(): void {
    window.setTimeout(() => {
      const input = this.scannerInput?.nativeElement;
      if (!input) return;
      input.focus();
      input.select();
    }, 100);
  }

  private upsertRow(item: TomaRow): void {
    const row = this.normalizarRow(item);
    const index = this.rows.findIndex((current) => Number(current.COD_TOMS) === Number(row.COD_TOMS));
    if (index >= 0) {
      this.rows[index] = row;
    } else {
      this.rows = [row, ...this.rows];
    }
  }

  private cargarProductosDetalle(rows: any[]): void {
    const productos = rows
      .flatMap((row) => this.parseDetalle(row?.DET_ORD))
      .filter((item) => item?.tipo === 'LABORATORIO (MATRIZ - ALBORADA - PORTETE - DURAN)' && item?.codigo);

    if (productos.length === 0) {
      this.detalleRows = [];
      this.loadingDetalle = false;
      return;
    }

    const requests = productos.map((producto) => {
      return this.laboratorioService.productoLaboratorioToma(this.token, producto.codigo).pipe(
        map((resp) => resp?.rows || []),
        catchError(() => of([]))
      );
    });

    forkJoin(requests).subscribe((responses) => {
      this.detalleRows = responses
        .flat()
        .filter((producto) => producto?.NOM_PRO !== 'TOMA DE MUESTRA');
      this.loadingDetalle = false;
    });
  }

  private parseDetalle(raw: string): any[] {
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private normalizarRow(row: TomaRow): TomaRow {
    return {
      ...row,
      COD_TOMS: row.COD_TOMS,
      DIR_SURC: row.DIR_SURC || row.sucursal || '',
      NOM_CLI: row.NOM_CLI || row.cliente || '',
      EST_TOMS: row.EST_TOMS || row.estado || '',
      NUM_ORD: row.NUM_ORD || row.orden || '',
      FECREG_TOMS: row.FECREG_TOMS || row.fecha || '',
      COD_ORD: row.COD_ORD || '',
      EST_TOM: row.EST_TOM,
    };
  }

  private matches(value: any, filter: string, strict = false): boolean {
    const normalizedValue = this.normalize(value);
    const normalizedFilter = this.normalize(filter);
    if (!normalizedFilter) return true;
    return strict
      ? normalizedValue === normalizedFilter
      : normalizedValue.includes(normalizedFilter);
  }

  private normalize(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private playTone(frequency: number, durationSeconds: number): void {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      this.audioContext = this.audioContext || new AudioContextClass();
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gain.gain.setValueAtTime(0.001, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.16, this.audioContext.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + durationSeconds);
      oscillator.connect(gain);
      gain.connect(this.audioContext.destination);
      oscillator.start();
      oscillator.stop(this.audioContext.currentTime + durationSeconds);
    } catch (_) {
      // Aviso auxiliar; no debe romper el flujo si el navegador bloquea audio.
    }
  }
}
